import React, { type ReactNode, type SVGProps, forwardRef } from 'react';

import { Portal } from '@ark-ui/react/portal';
import {
  type SelectItemIndicatorProps,
  Select as SelectPrimitive,
  createListCollection,
  useSelectContext,
  useSelectItemContext,
} from '@ark-ui/react/select';
import { Box, Icon, Separator } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import type { SystemStyleObject } from '@betterment/samba-styled-system/types';

type BaseCssProp = { css?: SystemStyleObject };

type MultiSelectProps<T> = Omit<SelectPrimitive.RootProps<T>, 'multiple' | 'closeOnSelect'>;

const Root = <T,>(props: MultiSelectProps<T>) => {
  const { children, ...rest } = props;
  return (
    <SelectPrimitive.Root multiple={true} closeOnSelect={false} {...rest}>
      {children}
      <SelectPrimitive.HiddenSelect />
    </SelectPrimitive.Root>
  );
};

// Override user agent stylesheet button text
const buttonColorReset: SystemStyleObject = css.raw({
  color: 'text',
});

export const triggerStyles = css.raw({
  ...buttonColorReset,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 'medium',
  border: 'token(borderWidths.thin) solid token(colors.border.input)',
  paddingY: 'space.150',
  paddingX: 'space.200',
  height: '48px',
  width: '100%',
  maxWidth: '100%',
  backgroundColor: 'background.surface',
  textStyle: 'text100',
  borderColor: 'border.input',
  _placeholder: { color: 'text.placeholder' },
  _focus: {
    borderColor: 'border.focused',
    outline: '1px solid token(colors.border.focused)',
  },
});

type TriggerElement = React.ElementRef<typeof SelectPrimitive.Trigger>;
type TriggerProps = SelectPrimitive.TriggerProps & BaseCssProp & { ref: HTMLButtonElement };

const Trigger = React.forwardRef<TriggerElement, TriggerProps>(({ css: cssProp, children, ...props }, forwardedRef) => {
  const className = css(triggerStyles, cssProp);
  return (
    <SelectPrimitive.Control data-samba-component={true}>
      <SelectPrimitive.Trigger data-samba-component={true} className={className} ref={forwardedRef} {...props}>
        {children}
        <SelectPrimitive.Indicator data-samba-component={true} asChild={true}>
          <Icon name="CaretDown" size="small" color="icon" />
        </SelectPrimitive.Indicator>
      </SelectPrimitive.Trigger>
    </SelectPrimitive.Control>
  );
});

type MultiValueTextRenderProps = {
  selectedItems: unknown[];
  valueAsString: string;
};

type MultiValueTextProps = {
  placeholder?: string;
  children?: (props: MultiValueTextRenderProps) => ReactNode;
};

const valueTextStyles = css({
  textStyle: 'text100',
  color: 'text',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&[data-placeholder="true"]': { color: 'text.weak' },
});

export const ValueText = forwardRef<HTMLSpanElement, SelectPrimitive.ValueTextProps>((props, ref) => {
  const { children, placeholder } = props;
  const select = useSelectContext();
  const { hasSelectedItems, valueAsString } = select;

  return (
    <span
      data-samba-component={true}
      data-placeholder={!hasSelectedItems}
      ref={ref}
      className={valueTextStyles}
      {...props}
    >
      {children || valueAsString || placeholder}
    </span>
  );
});

export const MultiValueText = forwardRef<HTMLSpanElement, MultiValueTextProps>((props, ref) => {
  const { children, placeholder } = props;
  const select = useSelectContext();
  const { selectedItems, valueAsString } = select;

  const value = children ? children({ selectedItems, valueAsString }) : valueAsString;

  return (
    <span data-placeholder={!value} ref={ref} className={valueTextStyles}>
      {value || placeholder}
    </span>
  );
});

const contentStyles = css.raw({
  minWidth: 'var(--reference-width)',
  backgroundColor: 'background.surface',
  borderRadius: 'medium',
  maxHeight: '300px',
  maxWidth: '400px',
  overflowY: 'hidden',
  border: 'token(borderWidths.thin) solid token(colors.border.input)',
  _focus: {
    outline: 'none',
  },
});

const wrapperStyles = css.raw({
  width: '100%',
  maxHeight: '300px',
  paddingY: 'space.100',
  overflowY: 'auto',
});

type ContentElement = React.ElementRef<typeof SelectPrimitive.Content>;
type ContentProps = SelectPrimitive.ContentProps & BaseCssProp;

const Content = React.forwardRef<ContentElement, ContentProps>(({ css: cssProp, children, ...props }, forwardedRef) => {
  const className = css(contentStyles, cssProp);
  return (
    <Portal data-samba-component={true}>
      <SelectPrimitive.Positioner data-samba-component={true}>
        <SelectPrimitive.Content data-samba-component={true} ref={forwardedRef} className={className} {...props}>
          <Box css={wrapperStyles}>{children}</Box>
        </SelectPrimitive.Content>
      </SelectPrimitive.Positioner>
    </Portal>
  );
});

const itemStyles = css.raw({
  outline: 'none',
  textStyle: 'text100',
  display: 'flex',
  gap: 'space.100',
  alignItems: 'start',
  paddingY: 'space.100',
  paddingX: 'space.200',
  position: 'relative',
  userSelect: 'none',
  cursor: 'default',

  _highlighted: { bg: 'background.surface.hover' },
});

const checkboxStyles = css.raw({
  backgroundColor: 'background.surface',
  width: 'size.250',
  height: 'size.250',
  borderRadius: 'medium',
  display: 'flex',
  flex: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'token(borderWidths.thin) solid token(colors.border.strong)',
  transition: 'background-color 0.1s ease, border 0.1s ease-in-out',
  color: 'icon.inverse',
  _disabled: {
    border: 'token(borderWidths.thin) solid token(colors.border.strong)',
    backgroundColor: 'background.input.disabled',
    color: 'icon.disabled',
  },
  '&[data-state=checked]': {
    backgroundColor: 'background.primary',
    borderWidth: '0px',
    _disabled: {
      border: 'token(borderWidths.thin) solid token(colors.border.strong)',
      backgroundColor: 'background.input.disabled',
    },
  },
});
const CustomCheckmark = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" {...props}>
    <path d="M395-277.348 218.348-455l57.891-57.891L395-395.131l288.761-287.76L741.652-624 395-277.348Z" />
  </svg>
);

const Checkbox = ({ isSelected, isDisabled = false }: { isSelected: boolean; isDisabled: boolean }) => {
  return (
    <Box
      data-state={isSelected ? 'checked' : 'unchecked'}
      data-disabled={isDisabled ? true : undefined}
      css={checkboxStyles}
    >
      <CustomCheckmark className={css({ fill: 'currentColor' })} />
    </Box>
  );
};

export const SelectItemIndicator = React.forwardRef<HTMLDivElement, SelectItemIndicatorProps>((_props, _ref) => {
  const itemProps = useSelectItemContext();

  return (
    <Box css={{ height: 'size.300', alignContent: 'center' }}>
      <Checkbox isSelected={itemProps.selected} isDisabled={itemProps.disabled} />
    </Box>
  );
});

type ItemElement = React.ElementRef<typeof SelectPrimitive.Item>;
type ItemProps = SelectPrimitive.ItemProps & BaseCssProp;

const Item = React.forwardRef<ItemElement, ItemProps>(({ css: cssProp, children, ...props }, forwardedRef) => {
  const className = css(itemStyles, cssProp);
  const select = useSelectContext();
  const { multiple } = select;

  return (
    <SelectPrimitive.Item className={className} ref={forwardedRef} {...props}>
      {multiple && <SelectItemIndicator />}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});

const labelStyles = css.raw({
  textStyle: 'text50',
  color: 'text.weak',
  paddingY: 'space.100',
  paddingX: 'space.200',
});

const ItemGroup = ({ children, ...props }: SelectPrimitive.ItemGroupProps) => {
  return (
    <SelectPrimitive.ItemGroup data-samba-component={true} {...props}>
      {children}
    </SelectPrimitive.ItemGroup>
  );
};

type ItemGroupLabelElement = React.ElementRef<typeof SelectPrimitive.ItemGroupLabel>;
type ItemGroupLabelProps = SelectPrimitive.ItemGroupLabelProps & BaseCssProp;

const ItemGroupLabel = React.forwardRef<ItemGroupLabelElement, ItemGroupLabelProps>(
  ({ css: cssProp, children, ...props }, forwardedRef) => {
    const className = css(labelStyles, cssProp);
    return (
      <SelectPrimitive.ItemGroupLabel data-samba-component={true} className={className} ref={forwardedRef} {...props}>
        {children}
      </SelectPrimitive.ItemGroupLabel>
    );
  }
);

const separatorStyles = css.raw({ fullWidthWithInset: 'space.200', bg: 'border' });

const ItemSeparator = ({ css: cssProp }: BaseCssProp) => {
  const className = css.raw(separatorStyles, cssProp);
  return <Separator css={className} />;
};

type DisplayNamed = { displayName?: string };
const CompositeComponent = Object.assign(Root, {
  Trigger,
  MultiValueText,
  ValueText,
  Content,
  ItemGroup,
  ItemGroupLabel,
  Item,
  Separator: ItemSeparator,
});

const MultiSelect = CompositeComponent;
(MultiSelect as DisplayNamed).displayName = 'MultiSelect';
MultiSelect.Trigger.displayName = 'MultiSelect.Trigger';
MultiSelect.MultiValueText.displayName = 'MultiSelect.MultiValueText';
MultiSelect.ValueText.displayName = 'MultiSelect.ValueText';
MultiSelect.Content.displayName = 'MultiSelect.Content';
(MultiSelect.ItemGroup as DisplayNamed).displayName = 'MultiSelect.ItemGroup';
MultiSelect.ItemGroupLabel.displayName = 'MultiSelect.ItemGroupLabel';
MultiSelect.Item.displayName = 'MultiSelect.Item';

export { MultiSelect, createListCollection as createMultiSelectCollection };
